/**
 * FHIR CodeSystems, ValueSets, and Extensions URLs
 *
 * This file contains centralized constants for FHIR system URLs used throughout the application.
 *
 * External URLs (HL7 base + terminology, US Core, IETF, SNOMED, LOINC, NUCC, RxNorm, CMS, etc.)
 * are re-exported from `@sf2/fhir-types/constants/external-urls` (canonical source).
 *
 * Alopex-owned URLs are re-exported from `@sf2/fhir-types/generated/canonical-urls`,
 * auto-generated from each IG resource's `url` field. To add a new Alopex URL constant
 * here: add the resource to the IG, run `npm run generate:terminologies`, then reference
 * `ALOPEX_STRUCTURE_DEFINITIONS.<URL_SLUG>` etc. SF2DEV-275 Decision #7.
 *
 * URL Structure:
 * - Standard FHIR: http://hl7.org/fhir/...
 * - HL7 Terminology: http://terminology.hl7.org/...
 * - US Core Extensions: http://hl7.org/fhir/us/core/StructureDefinition/...
 * - Custom (Alopex): http://alopexcare.com/...
 */

// External URL groups (canonical source: @sf2/fhir-types/constants/external-urls).
// Imported locally because helper functions below reference HL7_CODE_SYSTEMS; re-exported
// at module scope to preserve the existing public surface.
import {
  EXTERNAL_CODE_SYSTEMS,
  FHIR_IDENTIFIER_SYSTEMS,
  HL7_CODE_SYSTEMS,
  HL7_TERMINOLOGY as _EXT_HL7_TERMINOLOGY,
  US_CORE_CODE_SYSTEMS,
  US_CORE_EXTENSIONS,
} from '@sf2/fhir-types/constants/external-urls.js';
import { ALOPEX_IDENTIFIER_SYSTEMS } from '@sf2/fhir-types/constants/identifier-systems.js';
import {
  ALOPEX_ACTIVITY_DEFINITIONS,
  ALOPEX_CODE_SYSTEMS,
  ALOPEX_CONCEPT_MAPS,
  ALOPEX_STRUCTURE_DEFINITIONS,
  ALOPEX_VALUE_SETS,
} from '@sf2/fhir-types/generated/canonical-urls.js';

export { HL7_CODE_SYSTEMS, FHIR_IDENTIFIER_SYSTEMS, US_CORE_EXTENSIONS, US_CORE_CODE_SYSTEMS };

/**
 * Time Entry Extensions
 * Extensions for tracking time entries on Task resources
 */
export const TIME_ENTRY_EXTENSIONS = {
  /** Duration in minutes for time entries */
  DURATION_MINUTES: 'http://alopexcare.com/fhir/StructureDefinition/time-entry-duration',

  /** Time entry category (administrative, clinical, training, etc.) */
  TIME_CATEGORY: 'http://alopexcare.com/fhir/StructureDefinition/time-entry-category',

  /** Whether time is billable */
  IS_BILLABLE: 'http://alopexcare.com/fhir/StructureDefinition/time-entry-billable',

  /** Associated project/activity reference */
  PROJECT_REFERENCE: 'http://alopexcare.com/fhir/StructureDefinition/time-entry-project',

  /** Notes/description for the time entry */
  NOTES: 'http://alopexcare.com/fhir/StructureDefinition/time-entry-notes',

  /** Role attribution (care-coordinator | pharmacist | care-admin) on Time Entry and Daily Log Tasks */
  ROLE: ALOPEX_STRUCTURE_DEFINITIONS.TIME_ENTRY_ROLE,

  /** Allocation (eligible | ineligible) on Time Entry Tasks; billable is derived on read */
  ALLOCATION: ALOPEX_STRUCTURE_DEFINITIONS.TIME_ENTRY_ALLOCATION,
} as const;

/**
 * Time Tracking Role CodeSystem + codes
 * Bound to the time-entry-role extension.
 */
export const TIME_TRACKING_ROLE_SYSTEM = ALOPEX_CODE_SYSTEMS.TIME_TRACKING_ROLE;

export const TIME_TRACKING_ROLE_CODES = {
  CARE_COORDINATOR: 'care-coordinator',
  PHARMACIST: 'pharmacist',
  CARE_ADMIN: 'care-admin',
} as const;

export type TimeTrackingRole =
  (typeof TIME_TRACKING_ROLE_CODES)[keyof typeof TIME_TRACKING_ROLE_CODES];

/**
 * Time Tracking Allocation CodeSystem + codes
 * Bound to the time-entry-allocation extension. Two-state stamp; billable is
 * derived on read (cumulative eligible time vs the program's CPT thresholds).
 */
export const TIME_TRACKING_ALLOCATION_SYSTEM = ALOPEX_CODE_SYSTEMS.TIME_TRACKING_ALLOCATION;

export const TIME_TRACKING_ALLOCATION_CODES = {
  ELIGIBLE: 'eligible',
  INELIGIBLE: 'ineligible',
} as const;

export type TimeTrackingAllocation =
  (typeof TIME_TRACKING_ALLOCATION_CODES)[keyof typeof TIME_TRACKING_ALLOCATION_CODES];

/**
 * Time-tracking output-type codes written by the log-time bot to Monthly/Daily Log Tasks.
 * The system is the canonical task-output-type CodeSystem (SF2DEV-214 Pattern A).
 * Billable time is NOT persisted — it is derived on read.
 */
export const TASK_OUTPUT_TYPE_SYSTEM = ALOPEX_CODE_SYSTEMS.TASK_OUTPUT_TYPE;

export const TIME_TRACKING_OUTPUT_TYPE_CODES = {
  CC_TIME: 'cc-time',
  PHARMACIST_TIME: 'pharmacist-time',
  ELIGIBLE_TIME: 'eligible-time',
  INELIGIBLE_TIME: 'ineligible-time',
  CC_ELIGIBLE_TIME: 'cc-eligible-time',
  CC_INELIGIBLE_TIME: 'cc-ineligible-time',
  PHARMACIST_ELIGIBLE_TIME: 'pharmacist-eligible-time',
  PHARMACIST_INELIGIBLE_TIME: 'pharmacist-ineligible-time',
  CARE_ADMIN_INELIGIBLE_TIME: 'care-admin-ineligible-time',
} as const;

/**
 * Custom Organization Extensions
 * Application-specific extensions for data not covered by standard FHIR
 *
 * TODO: Replace 'alopexcare.com' with your actual organization's base URL
 * Example: 'https://fhir.mycompany.com/StructureDefinition/...'
 */
export const RELIGION_SYSTEM = _EXT_HL7_TERMINOLOGY.V3_RELIGIOUS_AFFILIATION;

export const SF1_RELIGION_SYSTEM = ALOPEX_CODE_SYSTEMS.SF1_RELIGIONS;

export const CUSTOM_EXTENSIONS = {
  /** Patient's religious affiliation (R4 core patient-religion extension) */
  RELIGION: 'http://hl7.org/fhir/StructureDefinition/patient-religion',

  /** Patient's IANA timezone identifier */
  TIMEZONE_ID: ALOPEX_STRUCTURE_DEFINITIONS.TIMEZONE_ID,

  /** Referral source */
  REFERRAL_SOURCE: 'http://alopexcare.com/referral-source',

  /** Referral date */
  REFERRAL_DATE: 'http://alopexcare.com/referral-date',

  /** Practice name */
  PRACTICE_NAME: 'http://alopexcare.com/practice-name',

  /** Facility name */
  FACILITY: 'http://alopexcare.com/facility',

  /** Facility (Location reference) — generic facility extension carrying Reference(Location) */
  FACILITY_REFERENCE: ALOPEX_STRUCTURE_DEFINITIONS.FACILITY,

  /** Transient read-only marker stamped by list bots when a catalog facility is soft-deleted (never persisted) */
  FACILITY_INACTIVE: 'http://alopexcare.com/facility-inactive',

  /** MCHC (Managed Care Health Center) */
  MCHC: 'http://alopexcare.com/mchc',

  /** Practitioner app permissions (comma-separated string; '*' = Super Admin) */
  APP_PERMISSIONS: ALOPEX_STRUCTURE_DEFINITIONS.PRACTITIONER_APP_PERMISSIONS,

  /** Practitioner professional summary/bio */
  PROFESSIONAL_SUMMARY: 'http://alopexcare.com/professional-summary',

  /** Practitioner work experience (complex extension) */
  WORK_EXPERIENCE: 'http://alopexcare.com/work-experience',

  /** Practitioner professional awards (complex extension) */
  PROFESSIONAL_AWARD: 'http://alopexcare.com/professional-award',

  /** Practitioner professional skills */
  PROFESSIONAL_SKILLS: 'http://alopexcare.com/professional-skills',

  /** Medical group affiliation marker */
  MEDICAL_GROUP_AFFILIATION: 'http://alopexcare.com/medical-group-affiliation',

  /** Legacy entity ID for organization references during migration */
  LEGACY_ENTITY_ID: 'http://alopexcare.com/legacy-entity-id',

  /** Task defer/snooze metadata (deferCount, lastDeferReason, lastDeferredAt) */
  TASK_DEFER_METADATA: ALOPEX_STRUCTURE_DEFINITIONS.TASK_DEFER_METADATA,

  /** Task urgency level (0-10 scale, separate from FHIR priority) */
  TASK_URGENCY: ALOPEX_STRUCTURE_DEFINITIONS.TASK_URGENCY,

  /** Task estimated duration */
  TASK_ESTIMATED_DURATION: ALOPEX_STRUCTURE_DEFINITIONS.TASK_ESTIMATED_DURATION,

  /** Task call center metadata (outcomeType, attemptNumber, outreachMethod, etc.) */
  TASK_CALL_METADATA: ALOPEX_STRUCTURE_DEFINITIONS.TASK_CALL_METADATA,

  /** Task checklist (ordered list of checklist items with completion tracking) */
  TASK_CHECKLIST: ALOPEX_STRUCTURE_DEFINITIONS.TASK_CHECKLIST,

  /** Priority score (1-150 integer for fine-grained worklist ordering) */
  PRIORITY_SCORE: ALOPEX_STRUCTURE_DEFINITIONS.PRIORITY_SCORE,

  /** Generation rationale (service + rationale for system-generated tasks) */
  GENERATION_RATIONALE: ALOPEX_STRUCTURE_DEFINITIONS.GENERATION_RATIONALE,

  /** Check severity for eligibility validation */
  CHECK_SEVERITY: ALOPEX_STRUCTURE_DEFINITIONS.CHECK_SEVERITY,

  /** Check blocking flag for eligibility validation */
  CHECK_BLOCKING: ALOPEX_STRUCTURE_DEFINITIONS.CHECK_BLOCKING,

  /** Qualified ICD codes from eligibility check */
  QUALIFIED_ICD_CODES: ALOPEX_STRUCTURE_DEFINITIONS.QUALIFIED_ICD_CODES,

  /** Unqualified ICD codes from eligibility check */
  UNQUALIFIED_ICD_CODES: ALOPEX_STRUCTURE_DEFINITIONS.UNQUALIFIED_ICD_CODES,

  /** Validation version for eligibility rules */
  VALIDATION_VERSION: ALOPEX_STRUCTURE_DEFINITIONS.VALIDATION_VERSION,

  /** Qualification result reference */
  QUALIFICATION_RESULT: ALOPEX_STRUCTURE_DEFINITIONS.QUALIFICATION_RESULT,

  /** Review metadata */
  REVIEW_METADATA: ALOPEX_STRUCTURE_DEFINITIONS.REVIEW_METADATA,

  /** Program ID extension */
  PROGRAM_ID: ALOPEX_STRUCTURE_DEFINITIONS.PROGRAM_ID,

  /** Contact notes on RelatedPerson */
  CONTACT_NOTES: ALOPEX_STRUCTURE_DEFINITIONS.CONTACT_NOTES,

  /** Proxy relationship on consent performer */
  PROXY_RELATIONSHIP: ALOPEX_STRUCTURE_DEFINITIONS.PROXY_RELATIONSHIP,

  /** Contact telecom extension */
  CONTACT_TELECOM: ALOPEX_STRUCTURE_DEFINITIONS.CONTACT_TELECOM,

  /** Enabled programs on Organization */
  ENABLED_PROGRAMS: ALOPEX_STRUCTURE_DEFINITIONS.ENABLED_PROGRAMS,

  /** Program code extension */
  PROGRAM_CODE: 'http://alopexcare.com/program-code',

  /** Status reason on EpisodeOfCare (why it's in current status) */
  STATUS_REASON: ALOPEX_STRUCTURE_DEFINITIONS.STATUS_REASON,

  /** Legacy referral ID for backward compatibility */
  LEGACY_REFERRAL_ID: 'http://alopexcare.com/legacy-referral-id',

  /** Last activity date on ServiceRequest (stamped on every referral save) */
  LAST_ACTIVITY_DATE: ALOPEX_STRUCTURE_DEFINITIONS.LAST_ACTIVITY_DATE,

  /** Whether an AccessPolicy requires NPI for assigned users */
  REQUIRES_NPI: 'http://alopexcare.com/fhir/Extension/requires-npi',

  /** Patient communication preference (phone, email, text, mail) */
  COMMUNICATION_PREFERENCE: ALOPEX_STRUCTURE_DEFINITIONS.COMMUNICATION_PREFERENCE,

  /** Patient preferred call times */
  PREFERRED_CALL_TIMES: ALOPEX_STRUCTURE_DEFINITIONS.PREFERRED_CALL_TIMES,

  CHRONICITY: ALOPEX_STRUCTURE_DEFINITIONS.CHRONICITY,

  /** Summary finding extension on Composition.section */
  SUMMARY_FINDING: ALOPEX_STRUCTURE_DEFINITIONS.SUMMARY_FINDING,

  /** Summary reporting period extension on Composition */
  SUMMARY_PERIOD: ALOPEX_STRUCTURE_DEFINITIONS.SUMMARY_PERIOD,

  /** Summary lifecycle status extension on Composition */
  SUMMARY_STATUS_EXT: ALOPEX_STRUCTURE_DEFINITIONS.SUMMARY_STATUS,

  /** Summary approval metadata extension on Composition */
  SUMMARY_APPROVAL: ALOPEX_STRUCTURE_DEFINITIONS.SUMMARY_APPROVAL,

  /** AI generation metadata extension on Composition */
  AI_GENERATION_METADATA: ALOPEX_STRUCTURE_DEFINITIONS.AI_GENERATION_METADATA,
} as const;

/**
 * Organization Type Codes
 * Codes for Organization.type to identify organization category
 * Reference: http://terminology.hl7.org/CodeSystem/organization-type
 */
export const ORGANIZATION_TYPE_CODES = {
  /** Pharmacy */
  PHARMACY: 'prov',

  /** Healthcare Provider */
  HEALTHCARE_PROVIDER: 'prov',
} as const;

/**
 * Organization Type System URL
 * Standard HL7 CodeSystem for organization types
 */
export const ORGANIZATION_TYPE_SYSTEM = _EXT_HL7_TERMINOLOGY.ORGANIZATION_TYPE;

/**
 * Custom Organization Identifier Systems
 * Custom systems for organization-specific identifiers
 */
export const CUSTOM_IDENTIFIER_SYSTEMS = {
  /** Alopex platform-wide medical record number */
  MRN: ALOPEX_IDENTIFIER_SYSTEMS.MRN,

  /** Referring provider or customer root medical record number */
  CUSTOMER_MRN: ALOPEX_IDENTIFIER_SYSTEMS.CUSTOMER_MRN,

  /** Legacy user ID for backward compatibility */
  USER_ID: 'http://alopexcare.com/user-id',

  /** Legacy entity ID for organization references */
  ENTITY_ID: 'http://alopexcare.com/entity-id',

  /** Provider ID (legacy system identifier for backward compatibility) */
  PROVIDER_ID: 'http://alopexcare.com/provider-id',

  /** Bot identifier system */
  BOT_IDENTIFIER: 'http://alopexcare.com/fhir/Bot',
} as const;

/**
 * Custom CodeSystems
 * Organization-specific code systems for custom data
 */
export const CUSTOM_CODE_SYSTEMS = {
  /** Service request categories (enrollment, etc.) */
  SERVICE_CATEGORY: 'http://alopexcare.com/service-category',

  /** Care programs (CCM, RPM, RTM, BHI) */
  PROGRAMS: ALOPEX_CODE_SYSTEMS.PROGRAM_TYPES,

  /** Referral status codes */
  REFERRAL_STATUS: 'http://alopexcare.com/referral-status',

  /** Episode of care type (program enrollment type) */
  EPISODE_TYPE: 'http://alopexcare.com/fhir/CodeSystem/episode-type',

  /** User roles (admin, care-coordinator, provider, etc.) */
  USER_ROLES: 'http://alopexcare.com/fhir/CodeSystem/user-roles',

  /** Practitioner kind marker (meta.tag) — distinguishes login users from no-login providers */
  PRACTITIONER_KIND: 'http://alopexcare.com/fhir/CodeSystem/practitioner-kind',

  /** Medical group affiliation types */
  AFFILIATION_TYPES: 'http://alopexcare.com/affiliation-types',

  /** Task types (ELIGIBILITY_REVIEW, CONSENT_CALL, etc.) */
  TASK_TYPES: ALOPEX_CODE_SYSTEMS.TASK_TYPES,

  /** Call outcome types for task call center metadata */
  CALL_OUTCOME_TYPES: ALOPEX_CODE_SYSTEMS.CALL_OUTCOME_TYPES,

  /** Outreach methods (phone, email, in_person) */
  OUTREACH_METHODS: ALOPEX_CODE_SYSTEMS.OUTREACH_METHODS,

  /**
   * Languages — BCP-47 language tags. The legacy Alopex `languages` CodeSystem was
   * deleted in SF2DEV-275 Decision #12 (standards drift fix); the canonical system is
   * now `urn:ietf:bcp:47`. Use `LANGUAGES` from `reference-data.ts` for the curated
   * Alopex subset, which is generated from the `common-languages` ValueSet.
   */
  LANGUAGES: 'urn:ietf:bcp:47',

  /** Religions */
  RELIGIONS: 'http://alopexcare.com/fhir/CodeSystem/religions',

  /** Eligibility status codes */
  ELIGIBILITY_STATUS: ALOPEX_CODE_SYSTEMS.ELIGIBILITY_STATUS_CODES,

  /** Check severity codes */
  CHECK_SEVERITY: ALOPEX_CODE_SYSTEMS.CHECK_SEVERITY_CODES,

  /** Observation types */
  OBSERVATION_TYPES: 'http://alopexcare.com/CodeSystem/observation-types',

  /** Eligibility types */
  ELIGIBILITY_TYPES: 'http://alopexcare.com/fhir/CodeSystem/eligibility-types',

  /** Relationships */
  RELATIONSHIPS: ALOPEX_CODE_SYSTEMS.RELATIONSHIPS,

  /** Consent types */
  CONSENT_TYPES: 'http://alopexcare.com/CodeSystem/consent-types',

  /** Consent parties */
  CONSENT_PARTIES: 'http://alopexcare.com/CodeSystem/consent-parties',

  /** Consent method */
  CONSENT_METHOD: 'http://alopexcare.com/CodeSystem/consent-method',

  /** Requested info categories */
  REQUESTED_INFO: 'http://alopexcare.com/CodeSystem/requested-info',

  /** Entity types for organizations */
  ENTITY_TYPES: ALOPEX_CODE_SYSTEMS.ENTITY_TYPES,

  /** Vital measurement methods */
  VITAL_METHODS: 'http://alopexcare.com/fhir/CodeSystem/vital-methods',

  /** Condition status codes */
  CONDITION_STATUS: 'http://alopexcare.com/CodeSystem/condition-status',

  /** Tracking categories */
  TRACKING_CATEGORIES: 'http://alopexcare.com/fhir/CodeSystem/tracking-categories',

  /** Tracking subcategories */
  TRACKING_SUBCATEGORIES: 'http://alopexcare.com/fhir/CodeSystem/tracking-subcategories',

  /** Flag source codes */
  FLAG_SOURCE: ALOPEX_CODE_SYSTEMS.FLAG_SOURCE_CODES,

  /** App permissions */
  APP_PERMISSIONS: ALOPEX_CODE_SYSTEMS.APP_PERMISSIONS,

  /** Care team roles */
  CARE_TEAM_ROLES: 'http://alopexcare.com/fhir/CodeSystem/care-team-roles',

  /** Eligibility status (simplified) */
  ELIGIBILITY_STATUS_SIMPLE: 'http://alopexcare.com/CodeSystem/eligibility-status',

  /** Episode of care status reasons (enrollment pipeline cancellation/hold reasons) */
  EPISODE_STATUS_REASON: ALOPEX_CODE_SYSTEMS.EPISODE_STATUS_REASON,

  /** Communication preference codes (phone, email, text, mail) */
  COMMUNICATION_PREFERENCE: ALOPEX_CODE_SYSTEMS.COMMUNICATION_PREFERENCE,

  /** Problem categories for care plan conditions */
  PROBLEM_CATEGORY: ALOPEX_CODE_SYSTEMS.PROBLEM_CATEGORY,

  /** Stedi payer directory (eligibility-enabled insurance providers) */
  STEDI_PAYERS: 'http://alopexcare.com/fhir/CodeSystem/stedi-payers',

  /** Provider roles within an organization (credential-based classification) */
  PROVIDER_ROLES: ALOPEX_CODE_SYSTEMS.PROVIDER_ROLES,

  /** Task type codes for care plan workflow tasks (action-card, evaluate-*, goal-gap, etc.) */
  TASK_TYPE: ALOPEX_CODE_SYSTEMS.TASK_TYPE,

  /** Task category codes (quality-preventive, ccm-compliance, etc.) */
  TASK_CATEGORY: ALOPEX_CODE_SYSTEMS.TASK_CATEGORY,

  /** Task input type codes (task-category discriminator) */
  TASK_INPUT_TYPE: ALOPEX_CODE_SYSTEMS.TASK_INPUT_TYPE,

  /** Task output type codes (completion-record) */
  TASK_OUTPUT_TYPE: ALOPEX_CODE_SYSTEMS.TASK_OUTPUT_TYPE,

  /** Document type codes (physician-care-summary, etc.) */
  DOCUMENT_TYPES: ALOPEX_CODE_SYSTEMS.DOCUMENT_TYPES,

  /** Summary section type codes (urgent, significant, ongoing, care-team, additional-notes) */
  SUMMARY_SECTION_TYPE: ALOPEX_CODE_SYSTEMS.SUMMARY_SECTION_TYPE,

  /** Summary finding category codes */
  SUMMARY_FINDING_CATEGORY: ALOPEX_CODE_SYSTEMS.SUMMARY_FINDING_CATEGORY,

  /** Summary trend status codes (stable, improving, unchanged, worsening) */
  SUMMARY_TREND_STATUS: ALOPEX_CODE_SYSTEMS.SUMMARY_TREND_STATUS,

  /** Summary lifecycle status codes (draft, in-review, approved, final, cancelled) */
  SUMMARY_STATUS: ALOPEX_CODE_SYSTEMS.SUMMARY_STATUS,

  /** Caregiver relationship type (Spouse, Child, Parent, ..., Employer, State Agency, Federal Agency, Other) */
  CAREGIVER_RELATIONSHIP_TYPE: ALOPEX_CODE_SYSTEMS.CAREGIVER_RELATIONSHIP_TYPE,

  /** Care Team Communication reasons (Fall, Injury, Change in Condition, ...) */
  COMMUNICATION_REASONS: ALOPEX_CODE_SYSTEMS.COMMUNICATION_REASONS,

  /** Communication medium — Alopex 'Other' extension code paired with v3-ParticipationMode */
  COMMUNICATION_MEDIUM_OTHER: ALOPEX_CODE_SYSTEMS.COMMUNICATION_MEDIUM_OTHER,

  /** Contact disposition (outreach outcome) — shared by Encounter and Communication */
  CONTACT_DISPOSITION: ALOPEX_CODE_SYSTEMS.CONTACT_DISPOSITION,

  /** Note category codes (SF1 migration categories + general-note for UI-created notes) */
  NOTE_CATEGORY: ALOPEX_CODE_SYSTEMS.NOTE_CATEGORY,
} as const;

/** Extension URL stamped on each DocumentReference version snapshot to track the editor */
export const NOTE_LAST_EDITOR_EXT = ALOPEX_STRUCTURE_DEFINITIONS.NOTE_LAST_EDITOR;

/**
 * HL7 standard CodeSystems used outside HL7_CODE_SYSTEMS (additions).
 * Subset of the canonical HL7_TERMINOLOGY in @sf2/fhir-types/constants/external-urls
 * exposed for the existing web public API shape.
 */
export const HL7_TERMINOLOGY = {
  /** v3-ParticipationMode — used for Communication.medium codes (PHONE, EMAILWRIT, FAXWRIT, FACE) */
  V3_PARTICIPATION_MODE: _EXT_HL7_TERMINOLOGY.V3_PARTICIPATION_MODE,
} as const;

/**
 * ValueSet URLs for the Care Team Communications surface
 */
export const CARE_TEAM_COMMUNICATION_VALUE_SETS = {
  /** Care Team Communication medium picklist (composite v3 + Alopex 'Other' with display overrides) */
  MEDIUM: ALOPEX_VALUE_SETS.CARE_TEAM_COMMUNICATION_MEDIUM,

  /** Care Team Communication reason picklist */
  REASONS: ALOPEX_VALUE_SETS.COMMUNICATION_REASONS,

  /** Outcome picklist subset of contact-disposition */
  OUTCOME: ALOPEX_VALUE_SETS.CONTACT_DISPOSITION_CARE_TEAM_COMMUNICATIONS,

  /** Family & Caregivers relationship picklist (binds Patient.contact.relationship on the Care Team page) */
  CAREGIVER_RELATIONSHIP_TYPE: ALOPEX_VALUE_SETS.CAREGIVER_RELATIONSHIP_TYPE,
} as const;

/**
 * ValueSet URLs for the Facility (Location catalog) surface
 */
export const FACILITY_VALUE_SETS = {
  /** Facility type binding for Location.type[facilityType] (subset of v3-RoleCode) */
  FACILITY_TYPE: ALOPEX_VALUE_SETS.FACILITY_TYPE,

  /** CMS Place of Service code binding for Location.type[placeOfService] */
  PLACE_OF_SERVICE: ALOPEX_VALUE_SETS.PLACE_OF_SERVICE,
} as const;

/**
 * External CodeSystem URLs referenced by the Facility ValueSets
 */
export const FACILITY_CODE_SYSTEMS = {
  /** HL7 v3-RoleCode — source for Location.type[facilityType] codings */
  V3_ROLE_CODE: _EXT_HL7_TERMINOLOGY.V3_ROLE_CODE,

  /** CMS Place of Service — source for Location.type[placeOfService] codings */
  CMS_PLACE_OF_SERVICE: EXTERNAL_CODE_SYSTEMS.CMS_PLACE_OF_SERVICE,
} as const;

/**
 * Care Team Communications extension URLs
 */
export const CARE_TEAM_COMMUNICATION_EXTENSIONS = {
  /** Outcome of an outreach attempt (also used on Encounter per TECH-PatientOutreach.md) */
  CONTACT_DISPOSITION: ALOPEX_STRUCTURE_DEFINITIONS.CONTACT_DISPOSITION,

  /** Contact point (phone / email / fax) used at time of communication; Type-driven UI label */
  COMMUNICATION_CONTACT_POINT: ALOPEX_STRUCTURE_DEFINITIONS.COMMUNICATION_CONTACT_POINT,

  /** Boolean flag indicating whether Communication.sent / .received carries a meaningful time component */
  COMMUNICATION_TIME_SPECIFIED: ALOPEX_STRUCTURE_DEFINITIONS.COMMUNICATION_TIME_SPECIFIED,

  /** Structured back-link from Communication to source care team participant */
  CARE_TEAM_MEMBER_LINK: ALOPEX_STRUCTURE_DEFINITIONS.CARE_TEAM_MEMBER_LINK,

  /** Persisted on create by log-care-team-communication; never overwritten on update. */
  CREATED_AT: 'http://alopexcare.com/fhir/StructureDefinition/communication-created-at',

  /** Persisted on create by log-care-team-communication; never overwritten on update. */
  CREATED_BY: 'http://alopexcare.com/fhir/StructureDefinition/communication-created-by',
} as const;

/**
 * Inpatient Encounter (alopex-inpatient-encounter) profile canonical URL
 */
export const ALOPEX_INPATIENT_ENCOUNTER_PROFILE =
  ALOPEX_STRUCTURE_DEFINITIONS.ALOPEX_INPATIENT_ENCOUNTER;

/**
 * Inpatient Encounter extension URLs
 */
export const ENCOUNTER_EXTENSIONS = {
  /** How the care coordinator learned of the admission (required) */
  NOTIFICATION_METHOD_ADMISSION:
    ALOPEX_STRUCTURE_DEFINITIONS.ENCOUNTER_NOTIFICATION_METHOD_ADMISSION,

  /** How the care coordinator learned of the discharge */
  NOTIFICATION_METHOD_DISCHARGE:
    ALOPEX_STRUCTURE_DEFINITIONS.ENCOUNTER_NOTIFICATION_METHOD_DISCHARGE,

  /** Computed 30-day readmission flag (boolean) */
  READMIT_30_DAY: ALOPEX_STRUCTURE_DEFINITIONS.ENCOUNTER_READMIT_30_DAY,

  /** Computed readmission same-cause flag (same | different | unknown) */
  READMIT_SAME_CAUSE: ALOPEX_STRUCTURE_DEFINITIONS.ENCOUNTER_READMIT_SAME_CAUSE,

  /** Free-text reason captured when status transitions to entered-in-error */
  ENTERED_IN_ERROR_REASON: ALOPEX_STRUCTURE_DEFINITIONS.ENCOUNTER_ENTERED_IN_ERROR_REASON,
} as const;

/**
 * Inpatient Encounter CodeSystem URLs
 */
export const ENCOUNTER_CODE_SYSTEMS = {
  /** Care-coordinator-facing visit type (bound to Encounter.type) */
  VISIT_TYPE: ALOPEX_CODE_SYSTEMS.ENCOUNTER_VISIT_TYPE,

  /** How the care coordinator learned of an admission or discharge */
  NOTIFICATION_METHOD: ALOPEX_CODE_SYSTEMS.ENCOUNTER_NOTIFICATION_METHOD,

  /** Where the patient went after discharge */
  DISCHARGE_DISPOSITION: ALOPEX_CODE_SYSTEMS.DISCHARGE_DISPOSITION,
} as const;

/**
 * Inpatient Encounter ValueSet URLs
 */
export const ENCOUNTER_VALUE_SETS = {
  /** Visit type picklist (binds Encounter.type) */
  VISIT_TYPE: ALOPEX_VALUE_SETS.ENCOUNTER_VISIT_TYPE,

  /** Notification method picklist (binds the admission/discharge notification extensions) */
  NOTIFICATION_METHOD: ALOPEX_VALUE_SETS.ENCOUNTER_NOTIFICATION_METHOD,

  /** Discharge disposition picklist (binds Encounter.hospitalization.dischargeDisposition) */
  DISCHARGE_DISPOSITION: ALOPEX_VALUE_SETS.DISCHARGE_DISPOSITION,
} as const;

export const ENCOUNTER_VISIT_TYPE_CODES = {
  ER: 'er',
  HOSPITAL: 'hospital',
  REHAB: 'rehab',
  URGENT_CARE: 'urgent-care',
} as const;

export const ENCOUNTER_NOTIFICATION_METHOD_CODES = {
  PATIENT: 'patient',
  CAREGIVER: 'caregiver',
  PROVIDER: 'provider',
  EHR: 'ehr',
  OTHER: 'other',
} as const;

export const ENCOUNTER_DISCHARGE_DISPOSITION_CODES = {
  ALF: 'alf',
  HOME: 'home',
  REHAB: 'rehab',
  LTC: 'ltc',
  OTHER: 'other',
} as const;

export const ENCOUNTER_READMIT_SAME_CAUSE_CODES = {
  SAME: 'same',
  DIFFERENT: 'different',
  UNKNOWN: 'unknown',
} as const;

/**
 * Patient Outreach profile canonical URLs (see designs/TECH-PatientOutreach.md)
 */
export const ALOPEX_OUTREACH_ENCOUNTER_PROFILE =
  ALOPEX_STRUCTURE_DEFINITIONS.ALOPEX_OUTREACH_ENCOUNTER;

export const ALOPEX_PATIENT_OUTREACH_COMMUNICATION_PROFILE =
  ALOPEX_STRUCTURE_DEFINITIONS.ALOPEX_PATIENT_OUTREACH_COMMUNICATION;

/**
 * Patient Outreach CodeSystem URLs
 */
export const OUTREACH_CODE_SYSTEMS = {
  /** Call-direction code for outreach voice Encounters (phone-inbound | phone-outbound) */
  ENCOUNTER_TYPE: ALOPEX_CODE_SYSTEMS.ENCOUNTER_TYPE,

  /** Verbatim Genesys wrap-up code catalog (script-synced); also the manual-voice outcome source */
  GENESYS_WRAPUP_CODE: ALOPEX_CODE_SYSTEMS.GENESYS_WRAPUP_CODE,

  /** Closed enum of ingestion route-to targets */
  OUTREACH_ROUTING: ALOPEX_CODE_SYSTEMS.OUTREACH_ROUTING,
} as const;

/** ConceptMap routing each Genesys wrap-up code to one outreach-routing target */
export const GENESYS_WRAPUP_ROUTING_CM = ALOPEX_CONCEPT_MAPS.GENESYS_WRAPUP_ROUTING;

/**
 * Patient Outreach ValueSet URLs
 */
export const OUTREACH_VALUE_SETS = {
  /** Call-direction picklist (binds Encounter.type on the outreach Encounter profile) */
  ENCOUNTER_TYPE: ALOPEX_VALUE_SETS.ENCOUNTER_TYPE,

  /** Outreach medium picklist (binds Communication.medium on the patient-outreach Communication profile) */
  PATIENT_OUTREACH_MEDIUM: ALOPEX_VALUE_SETS.PATIENT_OUTREACH_MEDIUM,

  /** Every outcome code that can appear on an outreach log entry (genesys-wrapup-code + contact-disposition) */
  OUTCOMES: ALOPEX_VALUE_SETS.OUTREACH_OUTCOMES,

  /** Manual-voice outcome picklist (patient-voice-routed wrap-up codes + contact-disposition 'other') */
  VOICE_OUTCOMES: ALOPEX_VALUE_SETS.OUTREACH_VOICE_OUTCOMES,

  /** Manual non-voice outcome picklist (curated contact-disposition subset; shared Mail/Other) */
  NONVOICE_OUTCOMES: ALOPEX_VALUE_SETS.OUTREACH_NONVOICE_OUTCOMES,

  /** Manual SMS outcome picklist (the non-voice subset plus 'Monthly Text Exchange Completed') */
  SMS_OUTCOMES: ALOPEX_VALUE_SETS.OUTREACH_SMS_OUTCOMES,

  /** Manual Email outcome picklist (the non-voice subset plus 'Monthly Email Exchange Completed') */
  EMAIL_OUTCOMES: ALOPEX_VALUE_SETS.OUTREACH_EMAIL_OUTCOMES,
} as const;

export const ENCOUNTER_TYPE_CODES = {
  PHONE_OUTBOUND: 'phone-outbound',
  PHONE_INBOUND: 'phone-inbound',
} as const;

/**
 * Canonical URL of the MedRec-create Task ActivityDefinition. Kept in sync
 * with `MEDREC_CREATE_URL` in `apps/medplum/bots/shared/fhir-constants.ts`.
 */
export const MEDREC_CREATE_URL = ALOPEX_ACTIVITY_DEFINITIONS.MEDREC_CREATE;

/**
 * Patient.contact role-flag extension URLs (Care Team Family & Caregivers)
 */
export const CAREGIVER_FLAG_EXTENSIONS = {
  /** Boolean flag marking the contact as the patient's Power of Attorney */
  POWER_OF_ATTORNEY: ALOPEX_STRUCTURE_DEFINITIONS.CAREGIVER_POWER_OF_ATTORNEY,

  /** Boolean flag marking the contact as an Emergency Contact */
  EMERGENCY_CONTACT: ALOPEX_STRUCTURE_DEFINITIONS.CAREGIVER_EMERGENCY_CONTACT,

  /** Boolean flag marking the contact as the patient's Next of Kin */
  NEXT_OF_KIN: ALOPEX_STRUCTURE_DEFINITIONS.CAREGIVER_NEXT_OF_KIN,

  /** Boolean flag marking the contact as the patient's Primary Contact (single-holder) */
  PRIMARY_CONTACT: ALOPEX_STRUCTURE_DEFINITIONS.CAREGIVER_PRIMARY_CONTACT,
} as const;

/**
 * Identifier Type Codes
 * Common codes from http://terminology.hl7.org/CodeSystem/v2-0203
 */
export const IDENTIFIER_TYPE_CODES = {
  /** Medical Record Number */
  MR: 'MR',

  /** Driver's License Number */
  DL: 'DL',

  /** Member Number */
  MB: 'MB',

  /** Patient Internal Identifier */
  PI: 'PI',

  /** National Provider Identifier */
  NPI: 'NPI',
} as const;

/**
 * Marital Status Codes
 * Common codes from http://terminology.hl7.org/CodeSystem/v3-MaritalStatus
 */
export const MARITAL_STATUS_CODES = {
  /** Annulled */
  ANNULLED: 'A',

  /** Divorced */
  DIVORCED: 'D',

  /** Interlocutory */
  INTERLOCUTORY: 'I',

  /** Legally Separated */
  LEGALLY_SEPARATED: 'L',

  /** Married */
  MARRIED: 'M',

  /** Polygamous */
  POLYGAMOUS: 'P',

  /** Never Married / Single */
  NEVER_MARRIED: 'S',

  /** Domestic Partner */
  DOMESTIC_PARTNER: 'T',

  /** Unmarried */
  UNMARRIED: 'U',

  /** Widowed */
  WIDOWED: 'W',

  /** Unknown */
  UNKNOWN: 'UNK',
} as const;

/**
 * LOINC Vital Signs Codes
 * Standard LOINC codes for common vital signs observations
 * Reference: https://loinc.org/
 */
export const LOINC_VITAL_SIGNS = {
  /** Blood Pressure Panel - 85354-9 */
  BLOOD_PRESSURE_PANEL: '85354-9',

  /** Systolic Blood Pressure - 8480-6 */
  BLOOD_PRESSURE_SYSTOLIC: '8480-6',

  /** Diastolic Blood Pressure - 8462-4 */
  BLOOD_PRESSURE_DIASTOLIC: '8462-4',

  /** Heart Rate - 8867-4 */
  HEART_RATE: '8867-4',

  /** Body Temperature - 8310-5 */
  BODY_TEMPERATURE: '8310-5',

  /** Respiratory Rate - 9279-1 */
  RESPIRATORY_RATE: '9279-1',

  /** Oxygen Saturation - 2708-6 */
  OXYGEN_SATURATION: '2708-6',

  /** Body Weight - 29463-7 */
  BODY_WEIGHT: '29463-7',

  /** Body Height - 8302-2 */
  BODY_HEIGHT: '8302-2',

  /** Body Mass Index (BMI) - 39156-5 */
  BMI: '39156-5',

  /** Blood Glucose - 2339-0 */
  BLOOD_GLUCOSE: '2339-0',

  /** Head Circumference - 9843-4 */
  HEAD_CIRCUMFERENCE: '9843-4',
} as const;

/**
 * Vital Sign Units (UCUM - Unified Code for Units of Measure)
 */
export const VITAL_SIGN_UNITS = {
  /** Millimeters of Mercury (for blood pressure) */
  MM_HG: 'mm[Hg]',

  /** Beats per minute (for heart rate) */
  BPM: '/min',

  /** Degrees Fahrenheit */
  DEGREES_F: '[degF]',

  /** Degrees Celsius */
  DEGREES_C: 'Cel',

  /** Percentage (for oxygen saturation) */
  PERCENT: '%',

  /** Kilograms */
  KILOGRAMS: 'kg',

  /** Pounds */
  POUNDS: '[lb_av]',

  /** Centimeters */
  CENTIMETERS: 'cm',

  /** Inches */
  INCHES: '[in_i]',

  /** Milligrams per deciliter (for blood glucose) */
  MG_DL: 'mg/dL',

  /** Kilograms per square meter (for BMI) */
  KG_M2: 'kg/m2',

  /** Breaths per minute */
  BREATHS_PER_MIN: '/min',
} as const;

/**
 * Helper function to get identifier type coding
 */
export function getIdentifierTypeCoding(code: keyof typeof IDENTIFIER_TYPE_CODES) {
  return {
    system: HL7_CODE_SYSTEMS.IDENTIFIER_TYPE,
    code: IDENTIFIER_TYPE_CODES[code],
  };
}

/**
 * Helper function to get marital status coding
 */
export function getMaritalStatusCoding(code: keyof typeof MARITAL_STATUS_CODES) {
  return {
    system: HL7_CODE_SYSTEMS.MARITAL_STATUS,
    code: MARITAL_STATUS_CODES[code],
  };
}

// ============================================================================
// TASK STATUS AND PRIORITY MAPPINGS
// ============================================================================

/**
 * Legacy Task Status to FHIR Task.status mapping
 * Maps internal task statuses to standard FHIR Task status codes
 */
export const TASK_STATUS_TO_FHIR = {
  pending: 'requested',
  assigned: 'accepted',
  in_progress: 'in-progress',
  completed: 'completed',
  cancelled: 'cancelled',
  deferred: 'on-hold',
} as const;

/**
 * FHIR Task.status to Legacy Task Status mapping (reverse)
 */
export const FHIR_TO_TASK_STATUS = {
  requested: 'pending',
  received: 'pending',
  accepted: 'assigned',
  rejected: 'cancelled',
  ready: 'assigned',
  'in-progress': 'in_progress',
  'on-hold': 'deferred',
  completed: 'completed',
  cancelled: 'cancelled',
  'entered-in-error': 'cancelled',
  failed: 'cancelled',
  draft: 'pending',
} as const;

/**
 * Task Status type for legacy system
 */
export type LegacyTaskStatus = keyof typeof TASK_STATUS_TO_FHIR;

/**
 * FHIR Task Status type
 */
export type FhirTaskStatus = (typeof TASK_STATUS_TO_FHIR)[LegacyTaskStatus];

/**
 * Maps legacy priority level (0-10) to FHIR Task.priority
 */
export function mapPriorityToFhir(priorityLevel: number | undefined) {
  if (priorityLevel === undefined || priorityLevel === null) return 'routine';
  if (priorityLevel <= 2) return 'routine';
  if (priorityLevel <= 5) return 'urgent';
  if (priorityLevel <= 8) return 'asap';
  return 'stat';
}

/**
 * Maps FHIR Task.priority to legacy priority level (0-10)
 */
export function mapFhirToPriority(fhirPriority: string | undefined) {
  switch (fhirPriority) {
    case 'stat':
      return 10;
    case 'asap':
      return 7;
    case 'urgent':
      return 4;
    case 'routine':
    default:
      return 1;
  }
}

/**
 * Task type codes from our custom CodeSystem
 */
export const TASK_TYPE_CODES = {
  ELIGIBILITY_REVIEW: 'ELIGIBILITY_REVIEW',
  CONSENT_CALL: 'CONSENT_CALL',
  OUTREACH_CALL: 'OUTREACH_CALL',
  FOLLOW_UP_CALL: 'FOLLOW_UP_CALL',
  DOCUMENT_REVIEW: 'DOCUMENT_REVIEW',
  ENROLLMENT_COMPLETION: 'ENROLLMENT_COMPLETION',
  CARE_COORDINATION: 'CARE_COORDINATION',
  ASSESSMENT: 'ASSESSMENT',
  CARE_PLAN_REVIEW: 'CARE_PLAN_REVIEW',
  GENERAL: 'GENERAL',
  /** Time entry for tracking work duration */
  TIME_ENTRY: 'TIME_ENTRY',
} as const;

/**
 * Helper to create task type CodeableConcept
 */
export function getTaskTypeCoding(code: keyof typeof TASK_TYPE_CODES) {
  return {
    system: CUSTOM_CODE_SYSTEMS.TASK_TYPES,
    code: TASK_TYPE_CODES[code],
  };
}

/**
 * Advance Directives — Code Systems and constants
 */
export const ADVANCE_DIRECTIVE_CODE_SYSTEMS = {
  SECTION: ALOPEX_CODE_SYSTEMS.ADVANCE_DIRECTIVE_SECTION,
  CAPACITY: ALOPEX_CODE_SYSTEMS.ADVANCE_DIRECTIVE_CAPACITY,
  TYPE: ALOPEX_CODE_SYSTEMS.ADVANCE_DIRECTIVE_TYPE,
  CODE_STATUS: ALOPEX_CODE_SYSTEMS.ADVANCE_DIRECTIVE_CODE_STATUS,
  TREATMENT_LIMITATION: ALOPEX_CODE_SYSTEMS.ADVANCE_DIRECTIVE_TREATMENT_LIMITATION,
} as const;

export const ADVANCE_DIRECTIVE_EXTENSIONS = {
  NEXT_REVIEW_DATE: ALOPEX_STRUCTURE_DEFINITIONS.ADVANCE_DIRECTIVE_NEXT_REVIEW_DATE,
} as const;

export const ADVANCE_DIRECTIVE_CAPACITY = {
  HAS_CAPACITY: { code: 'has-capacity', display: 'Has capacity' },
  DOES_NOT_HAVE_CAPACITY: {
    code: 'does-not-have-capacity',
    display: 'Does not have capacity',
  },
} as const;

export const ADVANCE_DIRECTIVE_TYPES: ReadonlyArray<{ code: string; display: string }> = [
  { code: 'advance-directive', display: 'Advance Directive' },
  { code: 'hcp', display: 'Health Care Proxy' },
  { code: 'mpoa', display: 'Medical Power of Attorney' },
  { code: 'dpoa', display: 'Durable Power of Attorney' },
  { code: 'molst', display: 'MOLST' },
  { code: 'polst', display: 'POLST' },
  { code: 'most', display: 'MOST' },
  { code: 'post', display: 'POST' },
  { code: 'living-will', display: 'Living Will' },
  { code: 'pad', display: 'Psychiatric Advance Directive (PAD)' },
  { code: 'guardianship', display: 'Guardianship Papers' },
  { code: 'healthcare-surrogate', display: 'Healthcare Surrogate Form' },
];

export const ADVANCE_DIRECTIVE_CODE_STATUSES: ReadonlyArray<{ code: string; display: string }> = [
  { code: 'full-code', display: 'Full Code' },
  { code: 'dnr', display: 'DNR' },
  { code: 'dni', display: 'DNI' },
  { code: 'cmo', display: 'CMO / Palliative Care' },
];

export const ADVANCE_DIRECTIVE_LIMITATIONS: ReadonlyArray<{ code: string; display: string }> = [
  { code: 'no-feeding-tube', display: 'No feeding tube' },
  { code: 'no-clysis', display: 'No clysis' },
  { code: 'no-weights', display: 'No weights' },
  { code: 'no-outside-consultations', display: 'No outside consultations' },
  { code: 'no-labs', display: 'No labs' },
  { code: 'no-iv-fluids', display: 'No IV fluids' },
  { code: 'no-dialysis', display: 'No dialysis' },
  { code: 'no-antibiotics', display: 'No antibiotics' },
  { code: 'no-diagnostics', display: 'No diagnostics' },
  { code: 'no-transfusions', display: 'No transfusions' },
  { code: 'no-hospitalizations', display: 'No hospitalizations' },
];

export const ADVANCE_DIRECTIVE_SECTIONS = {
  CAPACITY: 'capacity',
  KNOWN_DIRECTIVES: 'known-directives',
  CODE_STATUS: 'code-status',
  LIMITATIONS: 'limitations',
  NOTES: 'notes',
} as const;
